import { useCallback, useState } from "react";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { createQuotation, updateQuotationItems } from "~/services/quotationService";
import { useProfileStore } from "~/store/profileStore";
import { Address, Client } from "~/types/client";
import type { CreateQuotationItemInput } from "~/types/quotation";
import type { Product } from "~/types/product";

export interface QuotationOrderItem {
  id: string;
  product: Product;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
}

interface UseQuotationFormProps {
  client: Client;
  onSuccess?: (quotationId: string) => void;
}

export const useQuotationForm = ({ client, onSuccess }: UseQuotationFormProps) => {
  const { toast } = useToast();
  const { id: profileId } = useProfileStore();

  // Form state
  const [orderItems, setOrderItems] = useState<QuotationOrderItem[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [comments, setComments] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [validityDays, setValidityDays] = useState(15);
  const [editingItem, setEditingItem] = useState<QuotationOrderItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate total
  const total = orderItems.reduce((sum, item) => {
    const lineTotal =
      item.quantity * item.unitPrice * (1 - (item.discountPercent || 0) / 100);
    return sum + lineTotal;
  }, 0);

  // Add product to order
  // Note: ProductSelector passes 6 params (product, quantity, unitPrice, suggestedPrice, hasPriceRanges, discountPercent)
  const handleAddProduct = useCallback(
    (
      product: Product,
      quantity: number,
      unitPrice: number,
      _suggestedPrice: number,
      _hasPriceRanges: boolean,
      discountPercent = 0
    ) => {
      const newItem: QuotationOrderItem = {
        id: crypto.randomUUID(),
        product,
        quantity,
        unitPrice,
        discountPercent,
      };

      setOrderItems((prev) => [...prev, newItem]);
    },
    []
  );

  // Edit existing item
  const handleEditItem = useCallback(
    (itemId: string, quantity: number, unitPrice: number, discountPercent = 0) => {
      setOrderItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, quantity, unitPrice, discountPercent }
            : item
        )
      );
      setEditingItem(null);
    },
    []
  );

  // Remove item
  const handleRemoveItem = useCallback((itemId: string) => {
    setOrderItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  // Fetch address when selection changes
  const handleAddressChange = useCallback(async (addressId: string | null) => {
    setSelectedAddressId(addressId);

    if (!addressId) {
      setSelectedAddress(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("addresses")
        .select("*")
        .eq("id", addressId)
        .single();

      if (error) throw error;

      setSelectedAddress({
        id: data.id,
        clientId: data.client_id,
        street: data.street,
        number: data.number,
        complement: data.complement || undefined,
        city: data.city,
        state: data.state || undefined,
        comuna: data.comuna || undefined,
        isDefault: data.is_default || false,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      });
    } catch (error) {
      console.error("Error fetching address:", error);
      setSelectedAddress(null);
    }
  }, []);

  // Check if form is valid
  const isFormValid = orderItems.length > 0 && selectedAddressId !== null;

  // Save quotation
  const handleSaveQuotation = useCallback(async () => {
    if (!profileId) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para crear un presupuesto",
        variant: "destructive",
      });
      return;
    }

    if (orderItems.length === 0) {
      toast({
        title: "Error",
        description: "Debes agregar al menos un producto",
        variant: "destructive",
      });
      return;
    }

    if (!selectedAddressId) {
      toast({
        title: "Error",
        description: "Debes seleccionar una dirección de entrega",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get client's sales condition for payment terms
      let paymentTerms = 30;
      if (client.salesConditionId) {
        const { data: condition } = await supabase
          .from("sales_conditions")
          .select("maximum_days")
          .eq("id", client.salesConditionId)
          .single();

        if (condition) {
          paymentTerms = condition.maximum_days ?? 30;
        }
      }

      // Prepare items
      const items: CreateQuotationItemInput[] = orderItems.map((item) => ({
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        discount_percent: item.discountPercent,
      }));

      // Create quotation
      const quotation = await createQuotation(
        {
          client_id: String(client.id),
          delivery_address_id: selectedAddressId || undefined,
          delivery_street: selectedAddress?.street,
          delivery_number: selectedAddress?.number,
          delivery_city: selectedAddress?.city,
          delivery_state: selectedAddress?.state,
          delivery_comuna: selectedAddress?.comuna,
          payment_terms: paymentTerms,
          sales_condition_id: client.salesConditionId || undefined,
          validity_days: validityDays,
          comments: comments || undefined,
          internal_notes: internalNotes || undefined,
          items,
        },
        profileId
      );

      toast({
        title: "Presupuesto creado",
        description: `Presupuesto #${quotation.quotation_number} creado exitosamente`,
      });

      onSuccess?.(quotation.id);
    } catch (error) {
      console.error("Error creating quotation:", error);
      toast({
        title: "Error",
        description: "No se pudo crear el presupuesto. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    profileId,
    client,
    orderItems,
    selectedAddressId,
    selectedAddress,
    validityDays,
    comments,
    internalNotes,
    isFormValid,
    toast,
    onSuccess,
  ]);

  // Reset form
  const resetForm = useCallback(() => {
    setOrderItems([]);
    setSelectedAddressId(null);
    setSelectedAddress(null);
    setComments("");
    setInternalNotes("");
    setValidityDays(15);
    setEditingItem(null);
  }, []);

  return {
    // Items
    orderItems,
    total,
    editingItem,
    setEditingItem,
    handleAddProduct,
    handleEditItem,
    handleRemoveItem,

    // Address
    selectedAddressId,
    selectedAddress,
    handleAddressChange,

    // Form fields
    comments,
    setComments,
    internalNotes,
    setInternalNotes,
    validityDays,
    setValidityDays,

    // Form state
    isFormValid,
    isSubmitting,

    // Actions
    handleSaveQuotation,
    resetForm,
  };
};
